import { useTexture } from "@react-three/drei";
import { useMemo, useRef } from "react";
import * as THREE from "three";

export interface RoomFloorProps {
  /** Path to the floor texture */
  textureSrc: string;
  /** Radius where fade starts (default: 8) */
  fadeStart?: number;
  /** Radius where fade ends (fully black) (default: 25) */
  fadeEnd?: number;
}

export function RoomFloor({
  textureSrc,
  fadeStart = 8,
  fadeEnd = 25,
}: RoomFloorProps) {
  const texture = useTexture(textureSrc) as THREE.Texture;
  const materialRef = useRef<THREE.MeshStandardMaterial>(null);

  const configuredTexture = useMemo(() => {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(64, 64);
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.needsUpdate = true;
    return texture;
  }, [texture]);

  // Custom shader to fade floor to black at edges
  const onBeforeCompile = useMemo(() => {
    return (shader: THREE.WebGLProgramParametersWithUniforms) => {
      shader.uniforms.uFadeStart = { value: fadeStart };
      shader.uniforms.uFadeEnd = { value: fadeEnd };
      shader.uniforms.uCenterOffset = { value: new THREE.Vector2(0, -1) };

      // Add uniforms to vertex shader
      shader.vertexShader = shader.vertexShader.replace(
        "#include <common>",
        `#include <common>
        varying vec2 vWorldXZ;`,
      );

      // Pass world position to fragment
      shader.vertexShader = shader.vertexShader.replace(
        "#include <worldpos_vertex>",
        `#include <worldpos_vertex>
        vWorldXZ = worldPosition.xz;`,
      );

      // Add uniforms and varying to fragment shader
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <common>",
        `#include <common>
        uniform float uFadeStart;
        uniform float uFadeEnd;
        uniform vec2 uCenterOffset;
        varying vec2 vWorldXZ;`,
      );

      // Apply radial fade after all lighting calculations
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <dithering_fragment>",
        `#include <dithering_fragment>
        float dist = length(vWorldXZ - uCenterOffset);
        float fade = 1.0 - smoothstep(uFadeStart, uFadeEnd, dist);
        gl_FragColor.rgb *= fade;`,
      );
    };
  }, [fadeStart, fadeEnd]);

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -1.38, 1]}
      receiveShadow
    >
      <planeGeometry args={[100, 100]} />
      <meshStandardMaterial
        ref={materialRef}
        map={configuredTexture}
        roughness={0.95}
        metalness={0}
        fog={false}
        onBeforeCompile={onBeforeCompile}
      />
    </mesh>
  );
}
